import { useCallback } from 'react';

/**
 * Shared canvas utilities for scratch card components.
 *
 * Provides:
 *   initCanvasBackground(canvas, contextOptions?)
 *     Sets up DPR-aware dimensions and paints the grey gradient scratch layer.
 *     Returns the 2d rendering context.
 *
 *   getEventCoordinates(e, canvas)
 *     Converts a mouse or touch event to canvas-space coordinates.
 *
 *   drawScratch(canvas, x, y)
 *     Erases a circle at (x, y) using destination-out compositing to reveal
 *     the content underneath.
 */
const useScratchCanvas = () => {
  const initCanvasBackground = useCallback((canvas, contextOptions = {}) => {
    const context = canvas.getContext('2d', contextOptions);
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    context.scale(dpr, dpr);

    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#bbbbbb');
    gradient.addColorStop(0.5, '#e0e0e0');
    gradient.addColorStop(1, '#bbbbbb');
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    return context;
  }, []);

  const getEventCoordinates = useCallback((e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    let x, y;
    if (e.touches) {
      x = (e.touches[0].clientX - rect.left) * (canvas.width / rect.width);
      y = (e.touches[0].clientY - rect.top) * (canvas.height / rect.height);
    } else {
      x = (e.clientX - rect.left) * (canvas.width / rect.width);
      y = (e.clientY - rect.top) * (canvas.height / rect.height);
    }
    return { x, y };
  }, []);

  const drawScratch = useCallback((canvas, x, y) => {
    const context = canvas.getContext('2d');
    context.globalCompositeOperation = 'destination-out';
    context.beginPath();
    context.arc(x, y, 20, 0, 2 * Math.PI);
    context.fill();
  }, []);

  return { initCanvasBackground, getEventCoordinates, drawScratch };
};

export default useScratchCanvas;
